import { Button } from "components/DesignSystem/Button/Button";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Cross } from "components/icons/Cross";
import {
  OVERDUE_LABEL,
  TIMELINE_DEADLINE_LABEL,
  TIMELINE_DETAIL_STATUS_COLOR,
  TIMELINE_FILED_ON_LABEL,
  TIMELINE_STATUS,
} from "constants/filingsTimeline";
import { TimelineFilingRow } from "store/apis/filingsTimeline";
import { formatDate } from "utils/formatDate";
import { getFilingDetailStatus } from "utils/filingsTimeline/getFilingDetailStatus";
import { getTimelineStatus } from "utils/filingsTimeline/getTimelineStatus";
import { TimelineDetailAction } from "./TimelineDetailAction";
import { useFilingEntityName } from "./useFilingEntityName";

type TimelineFilingDetailProps = {
  filing: TimelineFilingRow | null;
  hint: string;
  onClose: () => void;
};

const PANEL_CLASS =
  "t-flex t-min-h-[60px] t-flex-wrap t-items-center t-justify-between t-gap-4 t-border-0 t-border-t t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey t-px-5 t-py-3";

export const TimelineFilingDetail = ({
  filing,
  hint,
  onClose,
}: TimelineFilingDetailProps) => {
  const entityName = useFilingEntityName(filing);

  if (!filing) {
    return (
      <div className={PANEL_CLASS}>
        <span className="t-text-body-sm t-font-light t-italic t-text-neutral-40">
          {hint}
        </span>
      </div>
    );
  }

  const status = getTimelineStatus(filing);

  const isLaunched = Boolean(filing.filing_id);

  const isFiled =
    Boolean(filing.filed_on) || status === TIMELINE_STATUS.COMPLETED;

  const dateLabel = isFiled
    ? `${TIMELINE_FILED_ON_LABEL}: ${formatDate(
        filing.filed_on || filing.deadline
      )}`
    : `${TIMELINE_DEADLINE_LABEL}: ${formatDate(filing.deadline)}`;

  return (
    <div className={PANEL_CLASS}>
      <div className="t-flex t-min-w-0 t-flex-wrap t-items-center t-gap-x-7 t-gap-y-1.5">
        <div className="t-flex t-min-w-0 t-flex-col">
          <span className="t-truncate t-text-subtext-sm t-text-text-60">
            {filing.name}
          </span>
          {entityName && (
            <span className="t-truncate t-text-body-sm t-text-neutral-40">
              {entityName}
            </span>
          )}
        </div>

        <span className="t-h-6 t-w-px t-shrink-0 t-bg-neutral-10" />

        <span className="t-flex t-shrink-0 t-items-center t-gap-1.5">
          <span className="t-text-body-sm t-text-text-30">Status:</span>
          <span className="t-flex t-items-center t-gap-1">
            <span
              className="t-h-1.5 t-w-1.5 t-shrink-0 t-rounded-full"
              style={{ backgroundColor: TIMELINE_DETAIL_STATUS_COLOR[status] }}
            />
            <span
              className="t-text-body-sm"
              style={{ color: TIMELINE_DETAIL_STATUS_COLOR[status] }}
            >
              {getFilingDetailStatus(filing)}
            </span>
          </span>
        </span>

        <span className="t-flex t-shrink-0 t-items-center t-gap-1">
          <span className="t-text-body-sm t-text-text-30">{dateLabel}</span>
          {filing.is_overdue && (
            <Tag tagType="red" size="small" icon rounded>
              {OVERDUE_LABEL}
            </Tag>
          )}
        </span>

        {isLaunched && filing.etd && (
          <span className="t-shrink-0 t-text-body-sm t-text-text-30">
            ETD: {formatDate(filing.etd)}
          </span>
        )}
      </div>

      <div className="t-flex t-shrink-0 t-items-center t-gap-1">
        <TimelineDetailAction filing={filing} />
        <Button
          customType="ghost_icon"
          size="xsmall"
          onClick={onClose}
          aria-label="Close filing details"
        >
          <span className="t-flex">
            <Cross size="12" />
          </span>
        </Button>
      </div>
    </div>
  );
};
